import { forwardRef, useEffect, useImperativeHandle, useRef } from "react"

export interface TejidoCanvasHandle {
  exportBlob: () => Promise<Blob | null>
  clear: () => void
}

interface TejidoCanvasProps {
  tipo: 'plano' | 'punto'
  disabled?: boolean
  onDrawingChange?: (hasDrawing: boolean) => void
  // URL de un dibujo ya guardado (al editar una prueba existente). Se carga sobre
  // el canvas para no perderlo: antes, al editar, el canvas siempre iniciaba en
  // blanco aunque ya hubiera un dibujo guardado.
  initialImageUrl?: string
}

function makeEmptyCells(rows: number, cols: number): boolean[][] {
  return Array.from({ length: rows }, () => new Array(cols).fill(false))
}

function drawBackground(ctx: CanvasRenderingContext2D, tipo: 'plano' | 'punto', width: number, height: number, cols: number, rows: number, cell: number, cells?: boolean[][]) {
  ctx.clearRect(0, 0, width, height)
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, width, height)
  if (tipo === 'plano') {
    ctx.strokeStyle = '#ccc'
    ctx.lineWidth = 1
    for (let c = 0; c <= cols; c++) {
      ctx.beginPath()
      ctx.moveTo(c * cell + 0.5, 0)
      ctx.lineTo(c * cell + 0.5, height)
      ctx.stroke()
    }
    for (let r = 0; r <= rows; r++) {
      ctx.beginPath()
      ctx.moveTo(0, r * cell + 0.5)
      ctx.lineTo(width, r * cell + 0.5)
      ctx.stroke()
    }
    if (cells) {
      ctx.fillStyle = '#000'
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          if (cells[r]?.[c]) ctx.fillRect(c * cell + 1, r * cell + 1, cell - 1, cell - 1)
        }
      }
    }
  } else {
    ctx.fillStyle = '#000'
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        ctx.beginPath()
        ctx.arc(c * cell + cell / 2, r * cell + cell / 2, 2, 0, Math.PI * 2)
        ctx.fill()
      }
    }
  }
}

export const TejidoCanvas = forwardRef<TejidoCanvasHandle, TejidoCanvasProps>(function TejidoCanvas(
  { tipo, disabled, onDrawingChange, initialImageUrl },
  ref
) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const drawingRef = useRef(false)
  const hasDrawnRef = useRef(false)
  const cellsRef = useRef<boolean[][]>([])
  const paintValueRef = useRef(true)
  const lastCellRef = useRef<{ row: number; col: number } | null>(null)

  const cols = tipo === 'plano' ? 40 : 16
  const rows = tipo === 'plano' ? 20 : 8
  const cell = tipo === 'plano' ? 14 : 24
  const width = cols * cell
  const height = rows * cell

  const render = () => {
    const ctx = canvasRef.current?.getContext('2d')
    if (!ctx) return
    drawBackground(ctx, tipo, width, height, cols, rows, cell, tipo === 'plano' ? cellsRef.current : undefined)
  }

  const updateHasDrawing = () => {
    const anyFilled = tipo === 'plano' && cellsRef.current.some(row => row.some(Boolean))
    if (hasDrawnRef.current !== anyFilled) {
      hasDrawnRef.current = anyFilled
      onDrawingChange?.(anyFilled)
    }
  }

  useEffect(() => {
    let cancelled = false
    cellsRef.current = makeEmptyCells(rows, cols)
    lastCellRef.current = null
    render()
    hasDrawnRef.current = false
    onDrawingChange?.(false)

    if (!initialImageUrl) return

    // Restaura un dibujo ya guardado (al editar una prueba existente) para que no
    // se pierda visualmente y para que, si el usuario guarda sin volver a dibujar,
    // se re-exporte y se conserve en vez de depender de que nadie toque el canvas.
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      if (cancelled) return
      const canvas = canvasRef.current
      const ctx = canvas?.getContext('2d')
      if (!canvas || !ctx) return

      try {
        if (tipo === 'plano') {
          // La cuadrícula no guarda las celdas pintadas por separado, solo la imagen
          // final. Se reconstruyen muestreando el centro de cada celda (el mismo punto
          // donde se pintó originalmente): así el usuario puede seguir editando celda
          // por celda sin que el próximo redibujado borre lo ya guardado.
          const off = document.createElement('canvas')
          off.width = width
          off.height = height
          const offCtx = off.getContext('2d')
          if (!offCtx) return
          offCtx.drawImage(img, 0, 0, width, height)
          const cells = makeEmptyCells(rows, cols)
          for (let r = 0; r < rows; r++) {
            for (let c = 0; c < cols; c++) {
              const px = offCtx.getImageData(Math.floor(c * cell + cell / 2), Math.floor(r * cell + cell / 2), 1, 1).data
              cells[r][c] = px[0] < 128 && px[1] < 128 && px[2] < 128
            }
          }
          cellsRef.current = cells
          render()
        } else {
          ctx.drawImage(img, 0, 0, width, height)
        }
      } catch (err) {
        // Canvas "tainted" (la imagen no permitió CORS): no se puede leer/reexportar,
        // se deja en blanco en vez de arriesgar un exportBlob corrupto o que falle.
        console.warn('No se pudo restaurar el dibujo guardado (posible bloqueo CORS):', err)
        return
      }

      hasDrawnRef.current = true
      onDrawingChange?.(true)
    }
    img.onerror = () => {
      // Imagen no disponible (CORS/red/borrada): se deja el canvas en blanco, sin
      // marcarlo como "con dibujo", para no exportar un lienzo vacío como si fuera
      // el dibujo guardado.
      console.warn('No se pudo cargar el dibujo guardado para editarlo:', initialImageUrl)
    }
    img.src = initialImageUrl

    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo, initialImageUrl])

  useImperativeHandle(ref, () => ({
    exportBlob: () =>
      new Promise(resolve => {
        const canvas = canvasRef.current
        if (!canvas) return resolve(null)
        canvas.toBlob(blob => resolve(blob), 'image/png')
      }),
    clear: () => {
      cellsRef.current = makeEmptyCells(rows, cols)
      lastCellRef.current = null
      render()
      hasDrawnRef.current = false
      onDrawingChange?.(false)
    },
  }))

  const getPos = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = canvasRef.current!.getBoundingClientRect()
    return {
      x: (e.clientX - rect.left) * (width / rect.width),
      y: (e.clientY - rect.top) * (height / rect.height),
    }
  }

  const getCell = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const { x, y } = getPos(e)
    const col = Math.floor(x / cell)
    const row = Math.floor(y / cell)
    if (col < 0 || col >= cols || row < 0 || row >= rows) return null
    return { row, col }
  }

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return
    canvas.setPointerCapture(e.pointerId)
    drawingRef.current = true

    if (tipo === 'plano') {
      const c = getCell(e)
      if (!c) return
      const newValue = !cellsRef.current[c.row][c.col]
      paintValueRef.current = newValue
      cellsRef.current[c.row][c.col] = newValue
      lastCellRef.current = c
      render()
      updateHasDrawing()
      return
    }

    const { x, y } = getPos(e)
    ctx.strokeStyle = '#000'
    ctx.lineWidth = 2
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.beginPath()
    ctx.moveTo(x, y)
  }

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled || !drawingRef.current) return

    if (tipo === 'plano') {
      const c = getCell(e)
      if (!c) return
      const last = lastCellRef.current
      if (last && last.row === c.row && last.col === c.col) return
      lastCellRef.current = c
      if (cellsRef.current[c.row][c.col] !== paintValueRef.current) {
        cellsRef.current[c.row][c.col] = paintValueRef.current
        render()
        updateHasDrawing()
      }
      return
    }

    const ctx = canvasRef.current?.getContext('2d')
    if (!ctx) return
    const { x, y } = getPos(e)
    ctx.lineTo(x, y)
    ctx.stroke()
    if (!hasDrawnRef.current) {
      hasDrawnRef.current = true
      onDrawingChange?.(true)
    }
  }

  const handlePointerUp = () => {
    drawingRef.current = false
    lastCellRef.current = null
  }

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className={`border border-foreground touch-none ${disabled ? '' : tipo === 'plano' ? 'cursor-pointer' : 'cursor-crosshair'}`}
      style={{ maxWidth: '100%' }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
    />
  )
})
